import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import type { ProviderConfig } from 'launchdarkly-react-client-sdk'

/** The provider component type returned by `asyncWithLDProvider`. */
type LDProviderComponent = Awaited<ReturnType<typeof asyncWithLDProvider>>

/**
 * Public, client-side LaunchDarkly ID, baked into the bundle at build time via
 * `VITE_LAUNCHDARKLY_CLIENT_ID`. Empty locally or before LaunchDarkly is
 * provisioned — the app then runs without the provider and every flag resolves
 * to its code default.
 */
export const LD_CLIENT_ID: string =
    import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID ?? ''

export const isLaunchDarklyEnabled: boolean = LD_CLIENT_ID !== ''

/**
 * Initial context used before the store/user are known. It is replaced by a
 * store-keyed multi-context via {@link LDIdentify} once AuthContext resolves.
 */
const ANONYMOUS_CONTEXT: ProviderConfig['context'] = {
    kind: 'user',
    key: 'anonymous',
    anonymous: true,
}

/**
 * Initialize the LaunchDarkly React provider before first render, seeding flags
 * from `localStorage` to avoid a flag-value flash on repeat visits.
 *
 * @returns The LDProvider component, or null when LaunchDarkly is disabled or
 * initialization fails. Callers then render the tree without it, so every flag
 * resolves to its code default.
 */
export async function initLaunchDarkly(): Promise<LDProviderComponent | null> {
    if (!isLaunchDarklyEnabled) {
        return null
    }

    try {
        return await asyncWithLDProvider({
            clientSideID: LD_CLIENT_ID,
            context: ANONYMOUS_CONTEXT,
            options: { bootstrap: 'localStorage' },
        })
    } catch {
        // Never let a LaunchDarkly outage block the app — fall back to defaults.
        return null
    }
}
